<script lang="ts">
  /**
   * Manage visualizations: upload your own (ISF shaders), hide ones you don't want in the pickers,
   * and keep ones out of Surprise me and Auto show. Choices are saved on this device.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { vizRole } from '../../shared/viz';
  import Icon from '../components/Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  let query = $state('');
  let kind = $state<'all' | 'native' | 'isf' | 'butterchurn' | 'hidden'>('all');
  let fileInput = $state<HTMLInputElement>();
  const uploaded = $derived(
    ctl.libraryVersion >= 0
      ? new Set(ctl.library.listVisualizations().map((v) => v.id))
      : new Set(),
  );

  const list = $derived.by(() => {
    const q = query.trim().toLowerCase();
    return ctl.allViz.filter((v) => {
      const k = v.kind ?? 'native';
      if (kind === 'hidden' ? !ctl.isHidden(v.id) : kind !== 'all' && k !== kind) return false;
      // There are hundreds of MilkDrop presets: list them only when asked for or searched.
      if (k === 'butterchurn' && kind !== 'butterchurn' && !q) return false;
      return (
        !q || `${v.name} ${v.description} ${(v.tags ?? []).join(' ')}`.toLowerCase().includes(q)
      );
    });
  });
  const shown = $derived(list.slice(0, 200));
  const counts = $derived({
    hidden: ctl.vizPrefs.hidden.length,
    noRandom: ctl.vizPrefs.noRandom.length,
  });

  const kindLabel = (k: string | undefined) =>
    k === 'isf' ? 'ISF' : k === 'butterchurn' ? 'MilkDrop' : 'Built-in';
</script>

<section class="sec" data-tour="viz-manage">
  <h2>Your visualizations</h2>
  <p class="hint first">
    Hide the ones you don’t want to see in the pickers (<Icon name="eye" />), and keep ones out of
    Surprise me and Auto show (<Icon name="dice" />). These choices are saved on this device.
  </p>
  <div class="row wrap">
    <button class="primary" data-testid="viz-upload" onclick={() => fileInput?.click()}
      >Upload a shader…</button
    >
    <span class="small muted">{counts.hidden} hidden · {counts.noRandom} kept out of random</span>
  </div>
  <input
    bind:this={fileInput}
    class="visually-hidden"
    type="file"
    accept=".fs,.frag,.isf,.glsl"
    multiple
    aria-label="Choose shader files"
    data-testid="viz-upload-input"
    onchange={(e) => {
      const f = [...(e.currentTarget.files ?? [])];
      e.currentTarget.value = '';
      void ctl.importFiles(f);
    }}
  />
  <p class="hint">
    Upload <a href="https://isf.video" target="_blank" rel="noopener">ISF</a> shaders (.fs). They’re
    checked for code that could hang the GPU before they run. Add <code>"Element"</code> to a shader’s
    CATEGORIES to file it under shapes &amp; particles. MilkDrop presets come from the verified pack in
    Settings → Visual (preset files contain code, so they can’t be uploaded).
  </p>
</section>

<section class="sec">
  <input
    type="search"
    class="search"
    placeholder="Search visualizations"
    bind:value={query}
    aria-label="Search visualizations"
    data-testid="viz-manage-search"
  />
  <div class="seg" role="group" aria-label="Show">
    {#each [['all', 'All'], ['native', 'Built-in'], ['isf', 'Shaders'], ['butterchurn', 'MilkDrop'], ['hidden', 'Hidden']] as [value, label] (value)}
      <button
        class:active={kind === value}
        aria-pressed={kind === value}
        onclick={() => (kind = value as typeof kind)}>{label}</button
      >
    {/each}
  </div>
  <div class="viz-list" data-testid="viz-manage-list">
    {#each shown as v (v.id)}
      {@const hidden = ctl.isHidden(v.id)}
      {@const random = ctl.inRandom(v.id)}
      <div class="viz-row" class:hidden data-testid={`manage-${v.id}`}>
        <div class="grow info">
          <div class="name">{v.name}</div>
          <div class="small muted">
            {kindLabel(v.kind)} · {vizRole(v) === 'element' ? 'shapes & particles' : 'full-screen'}
          </div>
        </div>
        <button
          class="icon-btn"
          class:on={!hidden}
          aria-pressed={!hidden}
          aria-label={hidden ? `Show ${v.name}` : `Hide ${v.name}`}
          title={hidden
            ? 'Hidden from the pickers (click to show)'
            : 'Shown in the pickers (click to hide)'}
          data-testid="viz-toggle-hidden"
          onclick={() => ctl.setVizHidden(v.id, !hidden)}
          ><Icon name={hidden ? 'eye-off' : 'eye'} /></button
        >
        <button
          class="icon-btn"
          class:on={random}
          aria-pressed={random}
          disabled={hidden}
          aria-label={random ? `Keep ${v.name} out of random` : `Allow ${v.name} in random`}
          title={random
            ? 'Can come up in Surprise me and Auto show (click to exclude)'
            : 'Never picked at random (click to allow)'}
          data-testid="viz-toggle-random"
          onclick={() => ctl.setVizRandom(v.id, !random)}><Icon name="dice" /></button
        >
        {#if uploaded.has(v.id)}
          <button
            class="icon-btn"
            aria-label={`Delete ${v.name}`}
            title="Delete this uploaded shader"
            onclick={() => ctl.deleteCustomViz(v.id)}><Icon name="trash" /></button
          >
        {/if}
      </div>
    {:else}
      <p class="hint">Nothing matches.</p>
    {/each}
    {#if list.length > shown.length}
      <p class="hint">{list.length - shown.length} more: search to narrow the list.</p>
    {/if}
  </div>
</section>

<style>
  .search {
    width: 100%;
    margin-bottom: 8px;
  }
  .seg {
    display: flex;
    gap: 4px;
    margin-bottom: 10px;
  }
  .seg button {
    flex: 1;
    font-size: 11.5px;
    padding: 3px 4px;
  }
  .viz-list {
    display: grid;
    gap: 2px;
  }
  .viz-row {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 6px;
    border-radius: var(--radius-sm);
  }
  .viz-row:hover {
    background: rgba(255, 255, 255, 0.04);
  }
  .viz-row.hidden .info {
    opacity: 0.45;
  }
  .name {
    font-size: 13px;
  }
  .icon-btn:not(.on) {
    opacity: 0.45;
  }
  code {
    font-family: var(--mono);
    font-size: 11.5px;
  }
</style>
